import { useState } from "react";
import {
  Box, Chip, IconButton, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SourceDetail from "@/components/VulnerabilityWatch/Sources/SourceDetail.tsx";
import type { PortalSourcesPage, SourceRow } from "@/types";
import { countryName } from "@/utils/countryName.ts";
import { formatAsn } from "@/utils/formatAsn.ts";
import { formatDateTime } from "@/utils/formatDateTime.ts";
import * as copy from "@/utils/vulnerabilityCopy.ts";

const WIDE = { display: { xs: "none", md: "table-cell" } } as const;
const COLUMNS = 9;
const VISUALLY_HIDDEN = {
  position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap",
} as const;

function GroupChips({ row, linesKnown }: { readonly row: SourceRow; readonly linesKnown: boolean }) {
  if (!linesKnown) return <Typography variant="body2" color="text.secondary">{copy.NOT_KNOWN}</Typography>;
  const groups = row.groups.map((group) => group.group);
  if (row.ungrouped_requests > 0) groups.push("notGrouped");
  if (groups.length === 0) return <Typography variant="body2" color="text.secondary">{copy.NOT_NAMED}</Typography>;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {groups.map((group) => <Chip key={group} size="small" variant="outlined" label={copy.groupLabel(group)} />)}
    </Box>
  );
}

function SourceRowView({ row, linesKnown }: { readonly row: SourceRow; readonly linesKnown: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            aria-label={open ? copy.hideDetailLabel(row.ip_address) : copy.showDetailLabel(row.ip_address)}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>{row.ip_address}</TableCell>
        <TableCell sx={WIDE}>{row.country_code ? countryName(row.country_code) : copy.NOT_KNOWN}</TableCell>
        <TableCell sx={WIDE}>{formatAsn(row.asn, row.as_name) ?? copy.NOT_KNOWN}</TableCell>
        <TableCell><GroupChips row={row} linesKnown={linesKnown} /></TableCell>
        <TableCell align="right">{linesKnown ? row.requests.toLocaleString("en-US") : copy.NOT_KNOWN}</TableCell>
        <TableCell>{copy.stepLabel(row.furthest_step)}</TableCell>
        <TableCell sx={WIDE}>{copy.answerLabel(row.answer)}</TableCell>
        <TableCell sx={WIDE}>{row.last_seen === null ? copy.NOT_KNOWN : formatDateTime(row.last_seen)}</TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={COLUMNS}><SourceDetail row={row} /></TableCell>
        </TableRow>
      )}
    </>
  );
}

function SourcesTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"><Box component="span" sx={VISUALLY_HIDDEN}>{copy.COL_DETAIL}</Box></TableCell>
        <TableCell>{copy.COL_ADDRESS}</TableCell>
        <TableCell sx={WIDE}>{copy.FILTER_COUNTRY}</TableCell>
        <TableCell sx={WIDE}>{copy.FILTER_NETWORK}</TableCell>
        <TableCell>{copy.FILTER_LOOKED_FOR}</TableCell>
        <TableCell align="right">{copy.COL_REQUESTS}</TableCell>
        <TableCell>{copy.FILTER_STEP}</TableCell>
        <TableCell sx={WIDE}>{copy.FILTER_ANSWER}</TableCell>
        <TableCell sx={WIDE}>{copy.COL_LAST_SEEN}</TableCell>
      </TableRow>
    </TableHead>
  );
}

export default function SourcesTable({ data, isLoading, linesKnown }: {
  readonly data: PortalSourcesPage | undefined;
  readonly isLoading: boolean;
  readonly linesKnown: boolean;
}) {
  if (isLoading) return <LinearProgress />;
  if (!data?.available || data.total === null) {
    return <Typography color="text.secondary" sx={{ p: 2 }}>{copy.NOT_KNOWN}</Typography>;
  }
  if (data.items.length === 0) return <Typography color="text.secondary" sx={{ p: 2 }}>{copy.NO_SOURCES}</Typography>;
  return (
    <TableContainer>
      <Table size="small">
        <SourcesTableHead />
        <TableBody>
          {data.items.map((row) => <SourceRowView key={row.ip_address} row={row} linesKnown={linesKnown} />)}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
